// Basit Üretim Reçetesi penceresi kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'inventory.recipe';

export const inventoryRecipeGuide = (t: TFunction): GuideContent => ({
  layout: 'tabbed',
  title: t(`${G}.title`, 'Reçete nasıl tanımlanır?'),
  intro: t(`${G}.intro`, 'Bu pencerede, ürün satıldığında stoktan düşecek malzemeleri ve miktarlarını tanımlarsınız.'),
  navItems: [
    t(`${G}.tabs.ingredients`, 'Malzemeler'),
    t(`${G}.tabs.branchCosts`, 'Şubelere Göre Maliyet'),
    t(`${G}.tabs.more`, 'Üretim sekmeleri'),
  ],
  regionLabels: {
    header: t(`${G}.regions.header`, 'Ürün ve reçete bilgisi'),
    tabs: t(`${G}.regions.tabs`, 'Sekmeler'),
    body: t(`${G}.regions.body`, 'Malzeme listesi'),
    footer: t(`${G}.regions.footer`, 'Sil / Yazdır / Kapat'),
  },
  steps: [
    {
      region: 'header',
      title: t(`${G}.s1.title`, '1. Reçeteyi oluşturun'),
      text: t(`${G}.s1.text`, 'Ürünün reçetesi yoksa reçete adını yazın; reçete kodu ve açıklama isteğe bağlıdır. "Reçete Oluştur" ile kaydedin. Reçete, satışta hangi malzemelerin stoktan düşeceğini belirler.'),
    },
    {
      region: 'body',
      title: t(`${G}.s2.title`, '2. Malzeme ekleyin'),
      text: t(`${G}.s2.text`, '"Malzeme Ekle" ile reçeteye ürün seçin. Seçilen her malzeme için miktar, fire oranı (%) ve isteğe bağlı not girip ekleyin.'),
      tips: [
        t(`${G}.s2.tip1`, 'Miktar: bir ürün satıldığında stoktan düşecek malzeme miktarıdır.'),
        t(`${G}.s2.tip2`, 'Fire oranı: işlemede oluşan kaybı ekler; "Fire Dahil" sütunu düşecek gerçek miktarı gösterir.'),
        t(`${G}.s2.tip3`, 'Kendi reçetesi olan malzeme "Alt Reçete" olarak görünür.'),
        t(`${G}.s2.tip4`, 'Üretim modülü açıksa malzeme adının yanındaki yıldızla bir "Ana malzeme" seçebilirsiniz (ör. Lahmacun harcında Kıyma). Üretim fişinde usta yalnız bu malzemenin miktarını yazar; çıktı ve diğer malzemeler orana göre hesaplanır. Yıldıza tekrar tıklamak işareti kaldırır; malzeme reçeteden silinirse işaret de kalkar.'),
        t(`${G}.s2.tip5`, '"Çıktıyı malzemelerden hesapla", aynı birim ailesindeki (kg/gr, lt/ml) malzeme miktarlarını çıktı birimine çevirip toplar ve "Bu reçete ile üretilen miktar" alanına yazar. Fire eklenmez: fire ürüne girmeyen kayıptır. Adet gibi çevrilemeyen satırlar hesaba katılmaz ve listelenir. Değeri elle değiştirebilir, "Kaydet" ile saklarsınız.'),
        t(`${G}.s2.tip6`, 'Üretim modülü açıksa "Çıkan miktar değişken (el işçiliği vb.)" kutusunu işaretleyebilirsiniz (ör. 18 kg hamurdan elle 250–280 hamur topu çıkar). İşaretliyse üretimi tamamlarken girdiler çıkan miktara oranlanmaz: hamur kullanıldığı gibi (18 kg) düşer, birim maliyet çıkan adede bölünür. "Beklenen en az / en çok" bu reçetenin bir partisi ("Bu reçete ile üretilen miktar") içindir ve fişte planlanan miktara göre oranlanır; dışında kalan miktarda yalnız uyarı verilir. "Kaydet" ile saklayın.'),
        t(`${G}.s2.tip7`, 'Alt reçetesi satışta/üretimde açılan bir malzeme ana malzeme olamaz; yıldız kırmızı görünür ve uyarı çıkar. Ana malzeme yapmak için malzemenin kartında üretim şeklini "Önce üretilip stoklanır" yapın ya da bu satırda tüketimi "Stoktan düş" seçin.'),
      ],
    },
    {
      region: 'header',
      title: t(`${G}.s3.title`, '3. Maliyeti izleyin'),
      text: t(`${G}.s3.text`, 'Alış fiyatı ve üretim maliyeti alanları, reçetenin toplam maliyetini görmenizi ve maliyet kontrolü yapmanızı kolaylaştırır. Altta "Toplam Üretim Maliyeti" (fire dahil) görünür.'),
      tips: [
        t(`${G}.s3.tip1`, '"Maliyet Şubesi" seçerseniz fiyatlar o şubenin alış fiyatından alınır; şubede fiyat yoksa ürün kartı fiyatı kullanılır. Boş bırakılırsa şirket geneli (ürün kartı) fiyatı kullanılır.'),
        t(`${G}.s3.tip2`, 'TL olmayan fiyatlar maliyete çevrilmeden toplanır; bu durumda uyarı etiketi görünür.'),
      ],
    },
    {
      region: 'tabs',
      title: t(`${G}.s4.title`, '4. Sekmeler'),
      text: t(`${G}.s4.text`, '"Malzemeler" reçete satırlarını, "Şubelere Göre Maliyet" aynı reçetenin her şubedeki maliyetini gösterir. Üretim modülü açıksa düz liste (stoktan düşecekler), operasyonlar, çıktılar ve versiyonlar sekmeleri de görünür.'),
    },
    {
      region: 'body',
      title: t(`${G}.s5.title`, '5. Satırları düzenleyin veya silin'),
      text: t(`${G}.s5.text`, 'Reçete oluşturulduktan sonra malzeme satırlarını "İşlemler" sütunundan güncelleyebilir veya (onay sonrası) silebilirsiniz.'),
    },
    {
      region: 'footer',
      title: t(`${G}.s6.title`, '6. Yazdırma ve reçeteyi silme'),
      text: t(`${G}.s6.text`, '"Yazdır" reçeteyi yazdırır. "Reçeteyi Sil" tüm malzemeler ve ayarlarla birlikte reçeteyi siler; bu işlem geri alınamaz. Başlıktaki (i) bu rehberi yeniden açar.'),
    },
  ],
});
